import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PlayerProfile from './PlayerProfile';

const StatHeader = ({ cols }) => (
  <tr className="text-[#d8ceb3] opacity-60 uppercase text-[9px] tracking-widest border-b border-[#658d71]">
    <th className="pb-1.5 pr-2 text-left font-bold">Player</th>
    {cols.map(c => (
      <th key={c} className="pb-1.5 px-1 text-center font-bold w-8">{c}</th>
    ))}
  </tr>
);

const BatterRow = ({ p, onClick }) => {
  const b = p.stats.batting;
  const avg = b.atBats > 0 ? (b.hits / b.atBats).toFixed(3).replace('0.', '.') : '.000';
  const isHot = b.hits >= 2 || b.homeRuns >= 1;
  return (
    <motion.tr
      className="border-b border-[rgba(101,141,113,0.25)] last:border-0 cursor-pointer group"
      whileHover={{ backgroundColor: 'rgba(216,206,179,0.07)' }}
      onClick={() => onClick(p)}
    >
      <td className="py-1.5 pr-2">
        <div className="flex items-center gap-1.5">
          {isHot && <span className="w-1 h-1 rounded-full bg-[#c4333e] shrink-0" />}
          <span className="text-[11px] font-bold text-white uppercase truncate max-w-[72px] group-hover:text-[#d8ceb3] transition-colors">
            {p.person.fullName.split(' ').pop()}
          </span>
        </div>
        <span className="text-[8px] text-[#658d71] font-bold uppercase tracking-wider">
          {p.position?.abbreviation || ''}
        </span>
      </td>
      <td className="py-1.5 px-1 text-center text-[11px] text-[#a9c9b1] tabular-nums">{b.atBats}</td>
      <td className="py-1.5 px-1 text-center text-[11px] text-white tabular-nums font-bold">{b.hits}</td>
      <td className="py-1.5 px-1 text-center text-[11px] text-white tabular-nums">{b.runs}</td>
      <td className="py-1.5 px-1 text-center text-[11px] text-white tabular-nums">{b.rbi}</td>
      <td className="py-1.5 px-1 text-center text-[11px] tabular-nums font-bold" style={{ color: b.homeRuns > 0 ? '#c4333e' : '#658d71' }}>
        {b.homeRuns > 0 ? b.homeRuns : '–'}
      </td>
      <td className="py-1.5 px-1 text-center text-[10px] text-[#a9c9b1] tabular-nums">{avg}</td>
    </motion.tr>
  );
};

const PitcherRow = ({ p, onClick }) => {
  const pit = p.stats.pitching;
  const isStarter = parseFloat(pit.inningsPitched) >= 4;
  return (
    <motion.tr
      className="border-b border-[rgba(101,141,113,0.25)] last:border-0 cursor-pointer group"
      whileHover={{ backgroundColor: 'rgba(216,206,179,0.07)' }}
      onClick={() => onClick(p)}
    >
      <td className="py-1.5 pr-2">
        <div className="flex items-center gap-1.5">
          {isStarter && <span className="text-[7px] bg-[#386646] text-[#a9c9b1] px-1 font-bold uppercase">SP</span>}
          <span className="text-[11px] font-bold text-white uppercase truncate max-w-[72px] group-hover:text-[#d8ceb3] transition-colors">
            {p.person.fullName.split(' ').pop()}
          </span>
        </div>
      </td>
      <td className="py-1.5 px-1 text-center text-[11px] text-[#d8ceb3] tabular-nums font-bold">{pit.inningsPitched}</td>
      <td className="py-1.5 px-1 text-center text-[11px] text-white tabular-nums">{pit.hits}</td>
      <td className="py-1.5 px-1 text-center text-[11px] tabular-nums" style={{ color: pit.earnedRuns > 3 ? '#c4333e' : '#a9c9b1' }}>
        {pit.earnedRuns}
      </td>
      <td className="py-1.5 px-1 text-center text-[11px] text-white tabular-nums">{pit.baseOnBalls}</td>
      <td className="py-1.5 px-1 text-center text-[11px] text-[#d8ceb3] tabular-nums font-bold">{pit.strikeOuts}</td>
      <td className="py-1.5 px-1 text-center text-[10px] text-[#a9c9b1] tabular-nums">{pit.pitchesThrown ?? '–'}</td>
    </motion.tr>
  );
};

const TeamPanel = ({ abbr, batters, pitchers, onPlayerClick }) => {
  const [view, setView] = useState('bat');
  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] flex flex-col">
      {/* Team header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
        <span className="text-sm font-black uppercase tracking-widest text-[#d8ceb3]">{abbr}</span>
        <div className="flex gap-1">
          {['bat', 'pit'].map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 border transition-colors ${
                view === v
                  ? 'bg-[#658d71] border-[#a9c9b1] text-white'
                  : 'border-[#658d71] text-[#a9c9b1] hover:text-white'
              }`}
            >
              {v === 'bat' ? 'Batting' : 'Pitching'}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto p-2">
        <AnimatePresence mode="wait">
          <motion.table
            key={view}
            className="w-full border-collapse"
            style={{ minWidth: '260px' }}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
          >
            <thead>
              {view === 'bat'
                ? <StatHeader cols={['AB', 'H', 'R', 'RBI', 'HR', 'AVG']} />
                : <StatHeader cols={['IP', 'H', 'ER', 'BB', 'K', 'P']} />
              }
            </thead>
            <tbody>
              {view === 'bat'
                ? batters.map((p, i) => <BatterRow key={i} p={p} onClick={onPlayerClick} />)
                : pitchers.map((p, i) => <PitcherRow key={i} p={p} onClick={onPlayerClick} />)
              }
            </tbody>
          </motion.table>
        </AnimatePresence>
      </div>

      <div className="px-3 pb-2 pt-0">
        <p className="text-[8px] text-[#658d71] tracking-widest uppercase">
          Click any player · Profile opens
        </p>
      </div>
    </div>
  );
};

const PlayerStats = ({ game }) => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  if (!game || !game.boxscore) return null;

  const getPlayers = (teamType) => {
    const playersMap = game.boxscore.teams[teamType].players;
    const all = Object.values(playersMap);

    const batters = all
      .filter(p => p.stats?.batting?.atBats > 0)
      .sort((a, b) => (b.stats.batting.hits + b.stats.batting.rbi) - (a.stats.batting.hits + a.stats.batting.rbi))
      .slice(0, 7);

    const pitchers = all
      .filter(p => p.stats?.pitching && parseFloat(p.stats.pitching.inningsPitched) > 0)
      .sort((a, b) => parseFloat(b.stats.pitching.inningsPitched) - parseFloat(a.stats.pitching.inningsPitched))
      .slice(0, 5);

    return { batters, pitchers };
  };

  const away = getPlayers('away');
  const home = getPlayers('home');
  const awayAbbr = game.teams.away.team.abbreviation || 'AWAY';
  const homeAbbr = game.teams.home.team.abbreviation || 'HOME';

  const handleClick = (p) => setSelectedPlayer(p);

  return (
    <>
      <div className="border-2 border-[#658d71] p-3 sm:p-5 bg-[rgba(0,0,0,0.05)] w-full">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-base sm:text-xl font-bold tracking-widest text-[#d8ceb3] uppercase">
              Player Stats
            </h3>
            <p className="text-[9px] text-[#658d71] tracking-widest uppercase mt-0.5">
              Click any player to view career profile
            </p>
          </div>
          <div className="flex items-center gap-2 text-[9px] text-[#658d71] uppercase tracking-widest font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#c4333e]" /> Hot game
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <TeamPanel
            abbr={awayAbbr}
            batters={away.batters}
            pitchers={away.pitchers}
            onPlayerClick={handleClick}
          />
          <TeamPanel
            abbr={homeAbbr}
            batters={home.batters}
            pitchers={home.pitchers}
            onPlayerClick={handleClick}
          />
        </div>
      </div>

      {selectedPlayer && (
        <PlayerProfile
          playerId={selectedPlayer.person.id}
          playerName={selectedPlayer.person.fullName}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </>
  );
};

export default PlayerStats;